import React, { useEffect, useRef, useState, useCallback } from "react";
import ImageAdjuster from "./ImageAdjuster";
import { useNavigate } from "react-router-dom";
import { AUTH_STORAGE_KEY, SERVER_URL, connectSocket } from "../socket";
import { QRCodeCanvas } from "qrcode.react";
import {
  linkAccount,
  getLinkedAccounts,
  removeLinkedAccount,
  switchAccount,
} from "../api/linkedAccounts";   // ← your new API file
import useContacts from "../hooks/useContacts";

// ─── helpers ─────────────────────────────────────────────────────────────────
const getAuth    = () => { try { return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY)) || {}; } catch { return {}; } };
const authHeader = () => ({ Authorization: "Bearer " + getAuth().token });

// ImageAdjuster moved to separate component: ./ImageAdjuster

// ─── Profile Modal ────────────────────────────────────────────────────────────
function ProfileModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const avatarRef = useRef();
  const coverRef  = useRef();
  const qrRef     = useRef();

  // ── Profile state ──
  const [profile, setProfile] = useState({ fullName: "", mobileNumber: "", profileNumber: "", profileNumberDisplay: "", profileImageUrl: "", coverImageUrl: "", email: "" });
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  // ── Image UI state ──
  const [avatarHover, setAvatarHover] = useState(false);
  const [coverHover,  setCoverHover]  = useState(false);
  const [avatarDrag,  setAvatarDrag]  = useState(false);
  const [coverDrag,   setCoverDrag]   = useState(false);
  const [adjuster,    setAdjuster]    = useState(null);

  // ── Multi-account state ──
  const [accounts,     setAccounts]     = useState([]);
  const [acctLoading,  setAcctLoading]  = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [addForm,      setAddForm]      = useState({ username: "", password: "" });
  const [addMsg,       setAddMsg]       = useState({ text: "", ok: false });
  const [addLoading,   setAddLoading]   = useState(false);
  const [switchingId,  setSwitchingId]  = useState(null);
  const [removingId,   setRemovingId]   = useState(null);

  const [showShare, setShowShare] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [shareSearch, setShareSearch] = useState("");
  const [qrImageData, setQrImageData] = useState(null);
  const [sharing, setSharing] = useState(false);

  const { contacts } = useContacts();

  // ── Load profile ──
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${window.SERVER_URL || ""}/api/profile`, { headers: authHeader() })
      .then(r => r.json())
      .then(d => setProfile({ ...d, coverImageUrl: d.coverImageUrl || "" }));
  }, [isOpen]);

  // ── Fetch linked accounts from DB ──
  const fetchAccounts = useCallback(async () => {
    setAcctLoading(true);
    try {
      const list = await getLinkedAccounts(authHeader());  // GET /api/accounts/linked
      setAccounts(list);
    } catch (err) {
      console.error("Failed to fetch linked accounts", err);
    }
    setAcctLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) fetchAccounts();
  }, [isOpen, fetchAccounts]);

  // ── Switch to a linked account ──
  const handleSwitch = async (acc) => {
    setSwitchingId(acc.linkId);
    try {
      const data = await switchAccount(acc.switchToken, authHeader()); // POST /api/accounts/switch
      if (data?.success) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token: data.token, user: data.user }));
        window.location.reload();
      } else {
        alert(data?.error || "Switch failed. Try re-adding the account.");
      }
    } catch (err) {
      console.error("Switch failed", err);
      alert("Network error during switch.");
    }
    setSwitchingId(null);
  };

  // ── Remove a linked account from DB ──
  const handleRemove = async (acc) => {
    setRemovingId(acc.linkId);
    try {
      await removeLinkedAccount(acc.linkId, authHeader()); // DELETE /api/accounts/linked/:linkId
      setAccounts(prev => prev.filter(a => a.linkId !== acc.linkId));
    } catch (err) {
      console.error("Remove failed", err);
      alert("Failed to remove account.");
    }
    setRemovingId(null);
  };

  // ── Link a new account ──
  const handleAddAccount = async (e) => {
    e.preventDefault();
    setAddMsg({ text: "", ok: false });
    if (!addForm.username || !addForm.password) {
      setAddMsg({ text: "Email and password are required.", ok: false });
      return;
    }
    setAddLoading(true);
    try {
      const account = await linkAccount(addForm.username, addForm.password, authHeader()); // POST /api/accounts/link
      if (account) {
        setAccounts(prev => prev.find(a => a.id === account.id) ? prev : [...prev, account]);
        setAddMsg({ text: "✓ Account linked successfully!", ok: true });
        setAddForm({ username: "", password: "" });
        setTimeout(() => { setAddMsg({ text: "", ok: false }); setShowAddForm(false); }, 1600);
      } else {
        setAddMsg({ text: "Linking failed. Check credentials.", ok: false });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || "Network error.";
      setAddMsg({ text: msg, ok: false });
    }
    setAddLoading(false);
  };

  // ── Profile save ──
  const handleChange = (e) => setProfile(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res  = await fetch(`${window.SERVER_URL || ""}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(profile),
      });
      const data = await res.json();
      if (data.success) { setProfile(data.user); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch {}
    setSaving(false);
  };

  // ── Image adjuster ──
  const openAdjuster = (file, type) => { if (!file || !file.type.startsWith("image/")) return; setAdjuster({ file, type, aspectRatio: type === "avatar" ? 1 : 360 / 140 }); };

  const handleAdjusterConfirm = async (croppedFile) => {
    const { type } = adjuster;
    setAdjuster(null);
    const fd = new FormData();
    fd.append(type === "avatar" ? "avatar" : "cover", croppedFile);
    const res  = await fetch(`${window.SERVER_URL || ""}/api/profile/${type === "avatar" ? "avatar" : "cover"}`, { method: "PUT", headers: authHeader(), body: fd });
    const data = await res.json();
    if (data.success) setProfile(p => ({ ...p, ...(type === "avatar" ? { profileImageUrl: data.profileImageUrl } : { coverImageUrl: data.coverImageUrl }) }));
  };

  const handleAvatar = (e) => { openAdjuster(e.target.files[0], "avatar"); e.target.value = ""; };
  const handleCover  = (e) => { openAdjuster(e.target.files[0], "cover");  e.target.value = ""; };

  const makeDragHandlers = (type, setDrag) => ({
    onDragOver:  (e) => { e.preventDefault(); setDrag(true); },
    onDragEnter: (e) => { e.preventDefault(); setDrag(true); },
    onDragLeave: (e) => { e.preventDefault(); setDrag(false); },
    onDrop:      (e) => { e.preventDefault(); setDrag(false); openAdjuster(e.dataTransfer.files[0], type); },
  });

  const coverDragHandlers  = makeDragHandlers("cover",  setCoverDrag);
  const avatarDragHandlers = makeDragHandlers("avatar", setAvatarDrag);

  const initials    = (profile.fullName || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const activeEmail = getAuth()?.user?.email;

  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 8, border: "1px solid #334155",  fontSize: 13, outline: "none" };

  const profileLink = `${window.location.origin}/profile?email=${encodeURIComponent(profile.email || "")}`;

  const getContactEmail = (contact) => {
    return (contact.emails?.[0] || contact.email || contact.contactId || contact.id || "").toLowerCase();
  };

  const filteredContacts = contacts
    .map((contact) => ({
      ...contact,
      email: getContactEmail(contact),
    }))
    .filter((contact) => contact.email)
    .filter((contact) => {
      const search = shareSearch.trim().toLowerCase();
      if (!search) return true;
      return (
        contact.email.includes(search) ||
        (contact.name || "").toLowerCase().includes(search)
      );
    });

  const toggleContactSelection = (email) => {
    setSelectedContacts((prev) =>
      prev.includes(email) ? prev.filter((item) => item !== email) : [...prev, email]
    );
  };

  const selectAllContacts = () => {
    setSelectedContacts(filteredContacts.map((contact) => contact.email));
  };

  const clearSelectedContacts = () => {
    setSelectedContacts([]);
  };

  const generateQRImage = async () => {
    if (!qrRef.current) return null;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const canvas = qrRef.current.querySelector("canvas");
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      setQrImageData(dataUrl);
      return dataUrl;
    }
    return null;
  };

  const handleShareToContacts = async () => {
    if (selectedContacts.length === 0) {
      alert("Select at least one contact to share the QR code.");
      return;
    }

    setSharing(true);
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed?.token || !parsed?.user?.email) {
        alert("Please sign in to share your profile.");
        setSharing(false);
        return;
      }

      const socket = connectSocket();
      if (!socket.connected) {
        await new Promise((resolve) => {
          if (socket.connected) {
            resolve();
          } else {
            socket.once("connect", resolve);
          }
        });
      }

      let imageData = qrImageData;
      if (!imageData) {
        imageData = await generateQRImage();
      }

      for (const email of selectedContacts) {
        const msg = {
          from: parsed.user.email,
          to: email,
          text: `Scan my profile QR to add me: ${profileLink}`,
          timestamp: Date.now(),
          clientId: String(Date.now() + Math.random()),
          attachmentUrl: imageData || undefined,
          type: imageData ? "qr" : "text",
        };
        socket.emit("chat_message", msg);
      }

      alert(`QR sent to ${selectedContacts.length} contact(s).`);
      setShowShare(false);
      setSelectedContacts([]);
      setShareSearch("");
    } catch (error) {
      console.error("Failed to share QR to contacts", error);
      alert("Unable to send QR to contacts.");
    } finally {
      setSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {adjuster && <ImageAdjuster file={adjuster.file} aspectRatio={adjuster.aspectRatio} onConfirm={handleAdjusterConfirm} onCancel={() => setAdjuster(null)} />}

      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", zIndex: 2000, display: "flex", alignItems: "stretch", justifyContent: "flex-end" }}>
        <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 400, height: "100%", background: "var(--bg-card,#111827)", borderRadius: "20px 0 0 20px", boxShadow: "-8px 0 40px rgba(0,0,0,0.5)", overflow: "hidden auto", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "'Segoe UI',system-ui,sans-serif", animation: "slideInRight 0.25s ease" }}>

          {/* ══ COVER ══ */}
          <div style={{ position: "relative", height: 140, cursor: "pointer", outline: coverDrag ? "3px dashed #f57011" : "none", outlineOffset: -3, transition: "outline 0.15s" }}
            onMouseEnter={() => setCoverHover(true)} onMouseLeave={() => setCoverHover(false)}
            onClick={() => coverRef.current?.click()} {...coverDragHandlers}>
            {profile.coverImageUrl
              ? <img src={profile.coverImageUrl} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#1e3a5f,#0f172a)" }} />}
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, opacity: (coverHover || coverDrag) ? 1 : 0, transition: "opacity 0.2s", pointerEvents: "none" }}>
              <span style={{ background: "rgba(245,112,17,0.9)", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600 }}>{coverDrag ? "⬇ Drop to adjust" : "📷 Change Cover"}</span>
              {!coverDrag && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>click or drag · then crop & zoom</span>}
            </div>
            <input ref={coverRef} type="file" hidden accept="image/*" onChange={handleCover} />
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: "absolute", top: 12, right: 12, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>

          {/* ══ AVATAR + NAME ══ */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, padding: "0 24px", marginTop: -44 }}>
            <div style={{ position: "relative", flexShrink: 0, outline: avatarDrag ? "3px dashed #f57011" : "none", borderRadius: "50%", transition: "outline 0.15s" }}
              onMouseEnter={() => setAvatarHover(true)} onMouseLeave={() => setAvatarHover(false)}
              onClick={() => avatarRef.current?.click()} {...avatarDragHandlers}>
              {profile.profileImageUrl
                ? <img src={profile.profileImageUrl} alt="avatar" style={{ width: 88, height: 88, borderRadius: "50%", border: "3px solid var(--bg-card,#111827)", objectFit: "cover", display: "block", cursor: "pointer" }} />
                : <div style={{ width: 88, height: 88, borderRadius: "50%", border: "3px solid var(--bg-card,#111827)", background: "linear-gradient(135deg,#f57011,#ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "#fff", cursor: "pointer" }}>{initials}</div>}
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, opacity: (avatarHover || avatarDrag) ? 1 : 0, cursor: "pointer", transition: "opacity 0.2s", pointerEvents: "none" }}>
                <span style={{ fontSize: 18 }}>📷</span>
                {avatarDrag && <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }}>DROP</span>}
              </div>
              <input ref={avatarRef} type="file" hidden accept="image/*" onChange={handleAvatar} />
            </div>
           <div style={{ paddingBottom: 6, minWidth: 0, flex: 1 }}>
  <div
    style={{
      fontSize: 18,
      marginTop: 50,
      fontWeight: 700,
      color: "var(--text-primary,#f1f5f9)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {profile.fullName || "—"}
  </div>

  <div
    style={{
      fontSize: 13,
      color: "var(--text-muted,#64748b)",
      marginTop: 2,
    }}
  >
    {profile.email || "No Email"}
  </div>

  {profile.profileNumberDisplay ? (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted,#94a3b8)",
          fontWeight: 500,
        }}
      >
        Kriva ID (Communication ID)
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#f59e0b",
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {profile.profileNumberDisplay}
      </div>
    </div>
  ) : null}
</div>
          </div>

          {/* ══ FORM ══ */}
          <div style={{ padding: "20px 24px 8px" }}>
            <div style={{ marginBottom: 14 }}>
              <label className="inputlablecss">Full Name</label>
              <input name="fullName" value={profile.fullName} onChange={handleChange} placeholder="Enter your full name" className="inputtexyboxcss" />
            </div>
            <div style={{ marginBottom: 20 }} style={{ display: "none"}}>
              <label className="inputlablecss">Mobile Number</label>
              <input name="mobileNumber" value={profile.mobileNumber} onChange={handleChange} placeholder="10-digit mobile number" className="inputtexyboxcss" />
            </div>
            <div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    Website
  </label>

  <input
    name="website"
    value={profile.website || ""}
    onChange={handleChange}
    placeholder="https://yourwebsite.com"
    className="inputtexyboxcss" 
  />
</div>

<div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    Facebook
  </label>

  <input
    name="facebook"
    value={profile.facebook || ""}
    onChange={handleChange}
    placeholder="Facebook link"
    className="inputtexyboxcss" 
  />
</div>

<div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    Instagram
  </label>

  <input
    name="instagram"
    value={profile.instagram || ""}
    onChange={handleChange}
    placeholder="Instagram link"
    className="inputtexyboxcss" 
  />
</div>

<div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    YouTube
  </label>

  <input
    name="youtube"
    value={profile.youtube || ""}
    onChange={handleChange}
    placeholder="YouTube link"
   className="inputtexyboxcss" 
  />
</div>

<div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    Twitter / X
  </label>

  <input
    name="twitter"
    value={profile.twitter || ""}
    onChange={handleChange}
    placeholder="Twitter link"
    className="inputtexyboxcss" 
  />
</div>

<div style={{ marginBottom: 14 }}>
  <label className="inputlablecss">
    About Us
  </label>

  <textarea
    name="aboutUs"
    value={profile.aboutUs || ""}
    onChange={handleChange}
    rows={5}
    placeholder="About your business"
    className="inputtexyboxcss" 
  />
</div>
            <div style={{ fontSize: 11, color: "var(--text-muted,#64748b)", marginBottom: 18 }}>💡 Click or drag onto cover/avatar · crop & zoom before saving</div>
            <button onClick={handleSave} disabled={saving} style={{ width: "100%", padding: "11px 0", borderRadius: 10, border: "none", background: saved ? "linear-gradient(135deg,#22c55e,#16a34a)" : "linear-gradient(135deg,#f57011,#ea6a0a)", color: "#fff", fontWeight: 700, fontSize: 15, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, transition: "background 0.3s", boxShadow: "0 4px 16px rgba(245,112,17,0.35)" }}>
              {saving ? "Saving…" : saved ? "✓ Saved!" : "Save Changes"}
            </button>
          </div>
          {/* <div style={{ marginBottom: 20 }}>

  <label style={{ display: "block", marginBottom: 8 }}>
    Catalog Upload
  </label>

  <input
    type="file"
    accept=".pdf,image/*"
    onChange={async (e) => {

      const file = e.target.files[0];

      if (!file) return;

      const fd = new FormData();

      fd.append("catalog", file);

      const res = await fetch(
        `${window.SERVER_URL || ""}/api/profile/catalog`,
        {
          method: "PUT",
          headers: authHeader(),
          body: fd
        }
      );

      const data = await res.json();

      if (data.success) {
        setProfile(p => ({
          ...p,
          catalogUrl: data.catalogUrl
        }));
      }

    }}
  />

  {profile.catalogUrl && (
    <a
      href={profile.catalogUrl}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "block",
        marginTop: 10,
        color: "#f57011"
      }}
    >
      View Catalog
    </a>
  )}

</div> */}

          {/* ══ MULTI-ACCOUNT SECTION ══ */}
          <div style={{ margin: "16px 24px 28px", borderRadius: 14, border: "1px solid #1e293b", overflow: "hidden" }}>

            {/* Collapse header */}
            <button
              onClick={() => { setShowAccounts(v => !v); if (!showAccounts) fetchAccounts(); }}
              style={{ width: "100%", padding: "13px 16px", background: "#0f172a", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ fontSize: 16 }}>👥</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>Manage Accounts</span>
                {accounts.length > 0 && (
                  <span style={{ background: "#f57011", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "1px 7px" }}>{accounts.length}</span>
                )}
              </div>
              <span style={{ color: "#64748b", fontSize: 12, display: "inline-block", transform: showAccounts ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▼</span>
            </button>
           
            {showAccounts && (
              <div style={{ background: "#0a0f1a", padding: "14px 16px 16px" }}>

                {/* Current account */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 7 }}>Current Account</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "rgba(245,112,17,0.07)", border: "1px solid rgba(245,112,17,0.2)" }}>
                    {profile.profileImageUrl
                      ? <img src={profile.profileImageUrl} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                      : <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#f57011,#ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>{initials}</div>}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#f1f5f9", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{profile.fullName || "—"}</div>
                      <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeEmail}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e", background: "rgba(34,197,94,0.1)", borderRadius: 6, padding: "2px 8px", flexShrink: 0 }}>ACTIVE</span>
                  </div>
                </div>

                {/* Linked accounts from DB */}
                {acctLoading ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#475569", fontSize: 13 }}>Loading…</div>
                ) : accounts.length > 0 ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 7 }}>Linked Accounts</div>
                    {accounts.map(acc => {
                      const ai = (acc.fullName || acc.email || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                      return (
                        <div key={acc.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#111827", border: "1px solid #1e293b", marginBottom: 8 }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#334155,#1e293b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#94a3b8", overflow: "hidden" }}>
                            {acc.profileImageUrl ? <img src={acc.profileImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : ai}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{acc.fullName || "—"}</div>
                            <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{acc.email}</div>
                          </div>
                          {/* calls POST /api/accounts/switch */}
                          <button onClick={() => handleSwitch(acc)} disabled={!!switchingId} style={{ padding: "5px 11px", borderRadius: 7, border: "none", background: switchingId === acc.linkId ? "#7c3f00" : "#f57011", color: "#fff", fontSize: 12, fontWeight: 700, cursor: switchingId ? "not-allowed" : "pointer", flexShrink: 0, opacity: switchingId === acc.linkId ? 0.7 : 1 }}>
                            {switchingId === acc.linkId ? "…" : "Switch"}
                          </button>
                          {/* calls DELETE /api/accounts/linked/:linkId */}
                          <button onClick={() => handleRemove(acc)} disabled={!!removingId} style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", color: "#ef4444", fontSize: 12, fontWeight: 700, cursor: removingId ? "not-allowed" : "pointer", flexShrink: 0, opacity: removingId === acc.linkId ? 0.5 : 1 }}>
                            {removingId === acc.linkId ? "…" : "✕"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "#475569", textAlign: "center", padding: "8px 0 14px" }}>No linked accounts yet.</div>
                )}

                {/* Add account form */}
                {!showAddForm ? (
                  <button onClick={() => setShowAddForm(true)} style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px dashed #334155", background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <span style={{ fontSize: 16 }}>＋</span> Add Another Account
                  </button>
                ) : (
                  <div style={{ background: "#111827", borderRadius: 10, padding: 14, border: "1px solid #1e293b" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>Link an Account</div>
                    <div style={{ fontSize: 11, color: "#475569", marginBottom: 12 }}>Credentials are verified by the server. Linked account will appear on all your devices.</div>
                    <input type="text" placeholder="Email" value={addForm.username} onChange={e => setAddForm(f => ({ ...f, username: e.target.value }))} style={{ ...inputStyle, marginBottom: 8 }} />
                    <input type="password" placeholder="Password" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} style={{ ...inputStyle, marginBottom: 10 }} />
                    {addMsg.text && (
                      <div style={{ fontSize: 12, marginBottom: 10, padding: "6px 10px", borderRadius: 7, color: addMsg.ok ? "#22c55e" : "#ef4444", background: addMsg.ok ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)", border: `1px solid ${addMsg.ok ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}` }}>
                        {addMsg.text}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setShowAddForm(false); setAddForm({ username: "", password: "" }); setAddMsg({ text: "", ok: false }); }} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid #334155", background: "transparent", color: "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                      <button onClick={handleAddAccount} disabled={addLoading} style={{ flex: 2, padding: "9px 0", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: addLoading ? "not-allowed" : "pointer", opacity: addLoading ? 0.7 : 1 }}>
                        {addLoading ? "Linking…" : "Link Account"}
                      </button>
                    </div>
                  </div>
                )}

                
              </div>
            )}
          </div>
            <button
              onClick={() => setShowShare(true)}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 10,
                border: "1px solid rgba(59,130,246,0.25)",
                background: "rgba(59,130,246,0.12)",
                color: "#ff700a",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                marginBottom: 12,
              }}
            >
              📷 Profile QR
            </button>
            <button
    onClick={() => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      navigate("/login");
      window.location.reload();
    }}
    style={{
      width: "100%",
      padding: "12px 0",
      borderRadius: 10,
      border: "1px solid rgba(239,68,68,0.25)",
      background: "rgba(239,68,68,0.1)",
      color: "#ef4444",
      fontSize: 14,
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    🚪 Sign Out
  </button>


        </div>
      </div>
      
      {showShare && (
        <div
          onClick={() => {
            setShowShare(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(90vw, 420px)",
              maxHeight: "80vh",
              background: "#0f172a",
              borderRadius: 18,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Share Profile QR</div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>Send your profile QR to saved contacts.</div>
              </div>
              <button
                onClick={() => {
                  setShowShare(false);
                }}
                style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 18, cursor: "pointer" }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginBottom: 16, padding: 12, background: "rgba(255,255,255,0.04)", borderRadius: 12 }}>
              <div ref={qrRef} style={{ width: 220, height: 220, borderRadius: 18, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 10 }}>
                <QRCodeCanvas value={profileLink} size={200} level="H" includeMargin />
              </div>
              <div style={{ fontSize: 12, color: "#cbd5e1", textAlign: "center" }}>
                Scan this QR to add my profile to contacts.
              </div>
            </div>

            <div style={{ marginBottom: 12, display: "flex", gap: 10, alignItems: "center" }}>
              <input
                placeholder="Search contacts"
                value={shareSearch}
                onChange={(e) => setShareSearch(e.target.value)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #334155", background: "#0f172a", color: "#f8fafc", fontSize: 13 }}
              />
              <button
                type="button"
                onClick={selectAllContacts}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #334155", background: "#1e293b", color: "#cbd5e1", fontSize: 12, cursor: "pointer" }}
              >
                Select all
              </button>
            </div>

            <div style={{ maxHeight: 220, minHeight: 220, overflowY: "auto", borderRadius: 14, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(148,163,184,0.08)", padding: 10, marginBottom: 16 }}>
              {filteredContacts.length === 0 ? (
                <div style={{ color: "#94a3b8", textAlign: "center", padding: 24, fontSize: 13 }}>
                  No contacts available.
                </div>
              ) : (
                filteredContacts.slice(0, 60).map((contact) => {
                  const email = contact.email;
                  const isSelected = selectedContacts.includes(email);
                  return (
                    <div
                      key={email}
                      onClick={() => toggleContactSelection(email)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: isSelected ? "rgba(37,99,235,0.15)" : "transparent",
                        cursor: "pointer",
                        marginBottom: 8,
                        border: "1px solid rgba(148,163,184,0.08)",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        style={{ cursor: "pointer", width: 16, height: 16 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#f8fafc", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {contact.name || email}
                        </div>
                        <div style={{ color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {email}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <button
                type="button"
                onClick={handleShareToContacts}
                disabled={sharing}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: sharing ? "not-allowed" : "pointer",
                  opacity: sharing ? 0.7 : 1,
                }}
              >
                {sharing ? "Sending…" : `Send to ${selectedContacts.length || "contacts"}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearSelectedContacts();
                  setShareSearch("");
                }}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  borderRadius: 12,
                  border: "1px solid #334155",
                  background: "transparent",
                  color: "#cbd5e1",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            </div>

            <button
              onClick={() => setShowShare(false)}
              style={{
                width: "100%",
                padding: "12px 0",
                borderRadius: 12,
                border: "none",
                background: "#111827",
                color: "#94a3b8",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ProfileModal;
